import { useQuery } from "@tanstack/react-query";
import { Plus, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";
import { eventSuffix } from "./calUtils";

const Chip = ({ e, onClick }) => (
  <button type="button" onClick={onClick} disabled={!onClick} data-testid={`day-event-${e.id}`}
    className="rounded px-2 py-0.5 text-xs transition-opacity hover:opacity-80 disabled:cursor-default" style={{ background: `${e.color}22`, color: e.color }}>
    {e.title}<span className="opacity-70">{eventSuffix(e)}</span>
  </button>
);

function Elsewhere({ uid, calendar, day }) {
  const { data } = useQuery({
    queryKey: ["cal", "convert", calendar.id, day.d],
    queryFn: () => api.post(`/universes/${uid}/calendars/convert`, { calendar_id: calendar.id, year: day.year, month: day.month, day: day.day }).then((r) => r.data),
  });
  const others = (data?.results || []).filter((r) => !r.is_source);
  if (!others.length) return null;
  return (
    <section data-testid="day-elsewhere">
      <div className="lf-label mb-2">On other calendars</div>
      <div className="space-y-2">
        {others.map((r) => (
          <div key={r.calendar_id} className="rounded-md border border-[#2D3442] px-3 py-2" data-testid={`day-elsewhere-${r.calendar_id}`}>
            <div className="flex items-center gap-2 text-sm">
              <span className="h-2 w-2 rounded-full" style={{ background: r.color }} />
              <span className="text-[#A8A090]">{r.name}</span>
            </div>
            <div className="mt-0.5 text-[#F3EBDD]">{r.out_of_range ? "Outside this calendar's range" : r.label}</div>
            {r.events?.length > 0 && <div className="mt-1.5 flex flex-wrap gap-1">{r.events.map((e, i) => <Chip key={`${e.id}-${i}`} e={e} />)}</div>}
          </div>
        ))}
      </div>
    </section>
  );
}

export const DayDialog = ({ open, onOpenChange, uid, calendar, day, canEdit, onAddEvent, onEditEvent, onAddTimelineEvent, onOpenTimelineEvent }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto lf-scroll" data-testid="cal-day-dialog">
      <DialogHeader><DialogTitle className="font-display text-3xl" data-testid="cal-day-title">{day.label}</DialogTitle></DialogHeader>
      <div className="space-y-5 pt-2">
        {day.moons.length > 0 && (
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#A8A090]" data-testid="day-moons">
            {day.moons.map((m) => <span key={m.index}>{m.name}: <span className="text-[#F3EBDD]">{m.event ? `${m.event === "full" ? "Full" : "New"} moon` : m.phase}</span></span>)}
          </div>
        )}
        <section>
          <div className="lf-label mb-2">Recurring events</div>
          {day.events.length === 0 ? <p className="text-sm text-[#A8A090]">Nothing on this day.</p>
            : <div className="flex flex-wrap gap-1.5">{day.events.map((e, i) => <Chip key={`${e.id}-${i}`} e={e} onClick={() => onEditEvent(e.id)} />)}</div>}
        </section>
        {(day.timeline_events.length > 0 || (calendar.link && day.t != null)) && (
          <section data-testid="day-timeline">
            <div className="lf-label mb-2 text-[#C4B5FD]">Timeline · T{day.t}</div>
            <div className="space-y-1">
              {day.timeline_events.map((e) => (
                <button key={e.id} type="button" onClick={() => onOpenTimelineEvent(calendar.link.timeline_id, e.timestamp_int)} data-testid={`day-tl-event-${e.id}`}
                  className="flex items-center gap-2 text-sm text-[#C4B5FD] hover:underline"><Clock className="h-3.5 w-3.5" />{e.title}</button>
              ))}
            </div>
          </section>
        )}
        <Elsewhere uid={uid} calendar={calendar} day={day} />
        {canEdit && (
          <div className="flex flex-wrap gap-2 border-t border-[#2D3442] pt-4">
            <Button size="sm" onClick={() => onAddEvent(day)} data-testid="day-add-event-button"><Plus className="mr-1 h-4 w-4" />Recurring event here</Button>
            {calendar.link && day.t != null && (
              <Button size="sm" variant="outline" className="border-[#8B5CF6]/50 text-[#C4B5FD]" data-testid="day-add-tl-event-button"
                onClick={() => onAddTimelineEvent({ timeline_id: calendar.link.timeline_id, timestamp_int: day.t, display_date: day.label })}>
                <Clock className="mr-1 h-4 w-4" />Timeline event here
              </Button>
            )}
          </div>
        )}
      </div>
    </DialogContent>
  </Dialog>
);
